import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import articleService from "services/articleService";

import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";

import { useAuth } from "auth/AuthProvider";
import { canEdit } from "auth/roles";
import CreateArticleDialog from "components/CreateArticleDialog";

function RecentArticles({ maxArticles = 2 }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const recent = await articleService.getRecentsArticles(maxArticles);
      setArticles(recent);
      setError(null);
    } catch (err) {
      setError("Impossible de charger les articles");
    } finally {
      setLoading(false);
    }
  }, [maxArticles]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Typography>Chargement des articles...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (articles.length === 0)
    return (
      <Card sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" component="h2">
            Nos derniers articles
          </Typography>
          {canEdit(user) && (
            <Tooltip title="Ajouter un article">
              <IconButton aria-label="Ajouter" onClick={() => setOpenCreate(true)}>
                <AddCircleOutline />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography>Aucun article récent</Typography>

        <CreateArticleDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onCreated={() => {
            setOpenCreate(false);
            load();
          }}
        />
      </Card>
    );

  return (
    <Card sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h4" component="h2">
          Nos derniers articles
        </Typography>
        {canEdit(user) && (
          <Tooltip title="Ajouter un article">
            <IconButton aria-label="Ajouter" onClick={() => setOpenCreate(true)}>
              <AddCircleOutline />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <List>
        {articles.map((article, index) => (
          <React.Fragment key={article.slug}>
            <ListItem
              button
              onClick={() => navigate(`/article/${article.slug}`)}
              sx={{ cursor: "pointer" }}
            >
              <List disablePadding>
                <ListItem disablePadding>
                  <Typography variant="h6">
                    Le {new Date(article.date).toLocaleDateString()}&nbsp;par&nbsp;
                    {article.author || "N/A"}
                  </Typography>
                </ListItem>
                <ListItem disablePadding>
                  <Typography variant="body2">{article.title}</Typography>
                </ListItem>
              </List>
            </ListItem>
            {index < articles.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <CreateArticleDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => {
          setOpenCreate(false);
          load();
        }}
      />
    </Card>
  );
}

RecentArticles.propTypes = {
  maxArticles: PropTypes.number,
};

export default RecentArticles;
